import { ChangeDetectionStrategy, Component, booleanAttribute, computed, input, output } from '@angular/core';
import { UiIconComponent } from '../icon/icon.component';
import { UiIconBadgeComponent, type UiIconBadgeTone } from '../icon-badge/icon-badge.component';

/**
 * Full-width actionable list row: leading icon-badge, title + optional subtitle,
 * trailing chevron. Extracted from the repeated "needs attention" / quick-action
 * rows. `tone` drives the badge (and the title when `emphasizeTitle` is set);
 * `divider` adds a bottom separator for stacked rows.
 */
@Component({
  selector: 'app-ui-action-row',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [UiIconComponent, UiIconBadgeComponent],
  template: `
    <button type="button" [class]="rowClasses()" (click)="action.emit()">
      <app-ui-icon-badge [icon]="icon()" [set]="iconSet()" [tone]="tone()" />
      <span class="min-w-0 flex-1">
        <span class="block truncate text-sm font-extrabold" [class]="titleClasses()">{{ title() }}</span>
        @if (subtitle()) {
          <span class="mt-0.5 block truncate text-xs text-text-muted">{{ subtitle() }}</span>
        }
      </span>
      <app-ui-icon class="h-6 w-6 shrink-0 text-text-muted" name="chevron-right" />
    </button>
  `,
})
export class UiActionRowComponent {
  readonly icon = input.required<string>();
  readonly iconSet = input<'app' | 'budget'>('app');
  readonly tone = input<UiIconBadgeTone>('neutral');
  readonly title = input.required<string>();
  readonly subtitle = input<string | null>(null);
  readonly divider = input(false, { transform: booleanAttribute });
  readonly emphasizeTitle = input(false, { transform: booleanAttribute });

  readonly action = output<void>();

  protected readonly rowClasses = computed(
    () =>
      `flex w-full cursor-pointer items-center gap-4 bg-transparent px-6 py-4 text-left${this.divider() ? ' border-b border-border-muted' : ''}`,
  );
  protected readonly titleClasses = computed(() =>
    this.emphasizeTitle() ? TITLE_TONE_CLASSES[this.tone()] : 'text-text-title',
  );
}

const TITLE_TONE_CLASSES: Record<UiIconBadgeTone, string> = {
  primary: 'text-brand-primary',
  accent: 'text-brand-accent',
  neutral: 'text-text-title',
  danger: 'text-status-danger',
  warn: 'text-status-warn',
};
